import type { NovoIconNames } from '@novonordisk/studyhub-novoui/vue'
import { defineStore } from 'pinia'
import { ref } from 'vue'

type ButtonVariants = 'fill' | 'outlineSubtle'

export type ApplicationError = {
  title?: string
  description?: string
  icon?: NovoIconNames
  active: boolean
  loading: boolean
  callback?: () => unknown
  buttonText?: string
  buttonStyle?: ButtonVariants
  persistent?: boolean
}

const errorDefaults: Partial<ApplicationError> = {
  title: 'Error',
  description: 'An unexpected error occurred',
  icon: 'alert',
  loading: false,
  persistent: false,
}

/**
 * This store contains the global error state, and is closely tied to the VApplicationAlert
 * component, which is responsible for rendering the error.
 *
 * @see VApplicationAlert.vue
 */
export const useErrorStore = defineStore('error', () => {
  const error = ref<ApplicationError>({
    title: undefined,
    description: undefined,
    icon: undefined,
    active: false,
    loading: false,
    persistent: false,
  })

  function setLoading(loading: boolean) {
    error.value = {
      ...error.value,
      loading,
    }
  }

  function showError(err?: Partial<Omit<ApplicationError, 'active' | 'loading'>>): void {
    error.value = {
      ...errorDefaults,
      ...(err ?? {}),
      active: true,
      loading: false,
    }
  }

  async function clearError(invokeCallback = true): Promise<void> {
    if (error.value.loading) return
    if (error.value.callback && invokeCallback) {
      await error.value.callback()
    }
    error.value.active = false
  }

  return { error, setLoading, showError, clearError }
})
